import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { deleteAdminCheckinPhoto, getAdminCheckin } from "../api/adminCheckins";
import { BACKEND_URL } from "../api/api";

const formatDate = (value) => value ? new Date(value).toLocaleDateString("nl-BE") : "-";
const formatDateTime = (value) => value ? new Date(value).toLocaleString("nl-BE") : "-";

export default function AdminCheckinDetail() {
  const { id } = useParams();
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const queryClient = useQueryClient();
  const { data: checkin, isLoading, isError } = useQuery({
    queryKey: ["admin", "checkin", id],
    queryFn: () => getAdminCheckin(id)
  });
  const deletePhotoMutation = useMutation({
    mutationFn: deleteAdminCheckinPhoto,
    onSuccess: () => {
      toast.success("Foto verwijderd");
      queryClient.invalidateQueries({ queryKey: ["admin", "checkin", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "checkins"] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Foto verwijderen mislukt");
    }
  });

  const removePhoto = (event, photoId) => {
    event.stopPropagation();
    if (window.confirm("Deze foto verwijderen?")) {
      deletePhotoMutation.mutate(photoId);
    }
  };

  if (isLoading) return <div className="loading-state">Check-in laden...</div>;

  if (isError || !checkin) {
    return (
      <section className="page">
        <div className="panel panel-pad">
          <h1 className="page-title">Check-in niet gevonden</h1>
          <Link className="button secondary" to="/admin/checkins">Terug naar check-ins</Link>
        </div>
      </section>
    );
  }

  const reservation = checkin.reservation;
  const trailer = checkin.trailer;
  const damagePhotos = (checkin.photos || []).filter((photo) => photo.type?.startsWith("damage"));
  const controlPhotos = (checkin.photos || []).filter((photo) => !photo.type?.startsWith("damage"));

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Check-in #{checkin.id}</h1>
          <p className="page-subtitle">Bevestigd op {formatDateTime(checkin.confirmedAt)}</p>
        </div>
        <Link className="button secondary" to="/admin/checkins">Terug</Link>
      </header>

      <div className="trailer-detail-layout">
        <section className="panel panel-pad">
          <div className="detail-section">
            <h2 className="detail-title">Reservatie</h2>
            <div className="detail-grid">
              <div className="detail-item"><div className="detail-label">Reservatie</div><div className="detail-value">#{checkin.reservationId}</div></div>
              <div className="detail-item"><div className="detail-label">Periode</div><div className="detail-value">{formatDate(reservation?.startDate)} tot {formatDate(reservation?.endDate)}</div></div>
              <div className="detail-item"><div className="detail-label">Status</div><div className="detail-value">{reservation?.status || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Ingecheckt</div><div className="detail-value">{formatDateTime(reservation?.checkInAt)}</div></div>
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Klant</h2>
            <div className="detail-grid">
              <div className="detail-item"><div className="detail-label">Naam</div><div className="detail-value">{reservation?.firstName || ""} {reservation?.name || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Telefoon</div><div className="detail-value">{reservation?.phone || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">E-mail</div><div className="detail-value">{reservation?.email || "-"}</div></div>
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Trailer</h2>
            <div className="detail-grid">
              <div className="detail-item"><div className="detail-label">Naam</div><div className="detail-value">{trailer?.name || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Status</div><div className="detail-value">{trailer?.status || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Issues</div><div className="detail-value">{checkin.hasIssues ? "Ja" : "Nee"}</div></div>
              <div className="detail-item"><div className="detail-label">Nieuwe schade</div><div className="detail-value">{checkin.hasDamage ? "Ja" : "Nee"}</div></div>
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Notities</h2>
            <p className="page-subtitle">{checkin.notes || "Geen notities opgegeven."}</p>
            {checkin.damageDescription && <p className="error-text">{checkin.damageDescription}</p>}
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Handtekening</h2>
            {checkin.checkInSignatureUrl ? (
              <img className="signature-image" src={`${BACKEND_URL}${checkin.checkInSignatureUrl}`} alt="Check-in handtekening" />
            ) : (
              <div className="empty-state">Geen handtekening opgeslagen.</div>
            )}
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Optie/accessoire controles</h2>
            {checkin.optionChecks?.length === 0 && <div className="empty-state">Geen opties gecontroleerd.</div>}
            <div className="list">
              {checkin.optionChecks?.map((item) => (
                <button key={item.id} className="list-row reservation-row" onClick={() => setPreviewPhoto({ imageUrl: item.photoUrl, label: item.optionName })}>
                  <div>
                    <div className="row-title">{item.optionName}</div>
                    <div className="row-meta">{item.status} {item.notes ? `- ${item.notes}` : ""}</div>
                  </div>
                  <span className={`badge ${item.status === "OK" ? "success" : "danger"}`}>{item.status}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <aside className="panel panel-pad detail-panel">
          <div className="detail-section">
            <h2 className="detail-title">Foto's</h2>
            {controlPhotos.length === 0 && <div className="empty-state">Geen controlefoto's gevonden.</div>}
            <div className="photo-grid">
              {controlPhotos.map((photo) => (
                <div key={photo.id} className="photo-thumb-wrap">
                  <button type="button" className="checkout-admin-photo" onClick={() => setPreviewPhoto(photo)}>
                    <img className="photo-thumb" src={`${BACKEND_URL}${photo.imageUrl}`} alt={photo.label} />
                    <span>{photo.label}</span>
                  </button>
                  <button
                    type="button"
                    className="photo-delete-button"
                    onClick={(event) => removePhoto(event, photo.id)}
                    aria-label="Foto verwijderen"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Nieuwe schade</h2>
            {!checkin.hasDamage && <div className="empty-state">Geen nieuwe schade gemeld.</div>}
            {checkin.hasDamage && (
              <>
                <p className="page-subtitle">{checkin.damageDescription || "Geen omschrijving"}</p>
                {damagePhotos.length === 0 && <div className="empty-state">Geen schadefoto's gevonden.</div>}
                <div className="photo-grid">
                  {damagePhotos.map((photo) => (
                    <div key={photo.id} className="photo-thumb-wrap">
                      <button type="button" className="checkout-admin-photo" onClick={() => setPreviewPhoto(photo)}>
                        <img className="photo-thumb" src={`${BACKEND_URL}${photo.imageUrl}`} alt={photo.label} />
                        <span>{photo.label}</span>
                      </button>
                      <button
                        type="button"
                        className="photo-delete-button"
                        onClick={(event) => removePhoto(event, photo.id)}
                        aria-label="Foto verwijderen"
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
      {previewPhoto && (
        <button className="image-modal" onClick={() => setPreviewPhoto(null)}>
          <span className="modal-close" aria-hidden="true">Sluiten</span>
          <figure className="checkout-photo-modal" onClick={(event) => event.stopPropagation()}>
            <img src={`${BACKEND_URL}${previewPhoto.imageUrl}`} alt={previewPhoto.label} />
            <figcaption>{previewPhoto.label || previewPhoto.type}</figcaption>
          </figure>
        </button>
      )}
    </section>
  );
}
